/**
 * P2PManager -- WebRTC peer-to-peer multiplayer.
 *
 * Flow:
 * 1. Host clicks "Crear Sala" -> generates offer string (base64)
 * 2. Host shares offer string with friend
 * 3. Friend clicks "Unirse" -> pastes offer -> generates answer string
 * 4. Host pastes answer -> connection established
 * 5. Data channel opens -> sync positions and block changes
 *
 * Max 2 players (host + 1 guest) for simplicity.
 */

export class P2PManager {
  constructor() {
    this.isHost = false;
    this.isConnected = false;
    this.peer = null; // RTCPeerConnection
    this.dataChannel = null;
    this.onMessage = null;  // callback({type, data})
    this.onConnect = null;
    this.onDisconnect = null;
    this._pendingCandidates = [];
    this._positionInterval = null;
  }

  /**
   * Host creates an RTCPeerConnection + data channel.
   * Waits for ICE gathering to complete, then returns a base64-encoded offer SDP.
   * @returns {Promise<string>} base64 offer
   */
  async createOffer() {
    this.isHost = true;
    this._cleanup();

    this.peer = new RTCPeerConnection({
      iceServers: [
        { urls: 'stun:stun.l.google.com:19302' },
        { urls: 'stun:stun1.l.google.com:19302' },
      ],
    });

    // Create the data channel (host side)
    this.dataChannel = this.peer.createDataChannel('game', {
      ordered: false,
      maxRetransmits: 0,
    });
    this._setupDataChannel(this.dataChannel);

    this.peer.onicecandidate = (e) => {
      if (e.candidate) this._pendingCandidates.push(e.candidate);
    };

    this.peer.onconnectionstatechange = () => {
      if (this.peer.connectionState === 'failed' || this.peer.connectionState === 'disconnected') {
        this._handleDisconnect();
      }
    };

    const offer = await this.peer.createOffer();
    await this.peer.setLocalDescription(offer);

    // Wait for ICE gathering to complete so the offer includes all candidates
    await this._waitForIceGathering();

    return btoa(JSON.stringify(this.peer.localDescription));
  }

  /**
   * Guest receives a base64 offer, creates an answer.
   * @param {string} offerB64
   * @returns {Promise<string>} base64 answer
   */
  async acceptOffer(offerB64) {
    this.isHost = false;
    this._cleanup();

    this.peer = new RTCPeerConnection({
      iceServers: [
        { urls: 'stun:stun.l.google.com:19302' },
        { urls: 'stun:stun1.l.google.com:19302' },
      ],
    });

    // Guest receives the data channel from the host
    this.peer.ondatachannel = (e) => {
      this.dataChannel = e.channel;
      this._setupDataChannel(this.dataChannel);
    };

    this.peer.onicecandidate = (e) => {
      if (e.candidate) this._pendingCandidates.push(e.candidate);
    };

    this.peer.onconnectionstatechange = () => {
      if (this.peer.connectionState === 'failed' || this.peer.connectionState === 'disconnected') {
        this._handleDisconnect();
      }
    };

    const offer = JSON.parse(atob(offerB64));
    await this.peer.setRemoteDescription(offer);

    const answer = await this.peer.createAnswer();
    await this.peer.setLocalDescription(answer);

    // Wait for ICE gathering to complete
    await this._waitForIceGathering();

    return btoa(JSON.stringify(this.peer.localDescription));
  }

  /**
   * Host accepts the guest's answer to complete the handshake.
   * @param {string} answerB64
   */
  async acceptAnswer(answerB64) {
    if (!this.peer) throw new Error('No hay conexion activa');
    const answer = JSON.parse(atob(answerB64));
    await this.peer.setRemoteDescription(answer);
  }

  /**
   * Wait for ICE gathering to finish (all candidates embedded in SDP).
   * Times out after 10 seconds.
   * @returns {Promise<void>}
   */
  _waitForIceGathering() {
    return new Promise((resolve) => {
      if (this.peer.iceGatheringState === 'complete') {
        resolve();
        return;
      }
      const timeout = setTimeout(() => {
        resolve(); // resolve anyway after timeout
      }, 10000);

      this.peer.onicegatheringstatechange = () => {
        if (this.peer.iceGatheringState === 'complete') {
          clearTimeout(timeout);
          resolve();
        }
      };
    });
  }

  /**
   * Wire up data channel event handlers.
   * @param {RTCDataChannel} channel
   */
  _setupDataChannel(channel) {
    channel.onopen = () => {
      this.isConnected = true;
      console.log('[P2P] Canal de datos abierto');
      if (this.onConnect) this.onConnect();
    };

    channel.onclose = () => {
      this._handleDisconnect();
    };

    channel.onerror = (err) => {
      console.warn('[P2P] Error en canal de datos:', err);
    };

    channel.onmessage = (e) => {
      try {
        const msg = JSON.parse(e.data);
        if (this.onMessage) this.onMessage(msg);
      } catch (err) {
        console.warn('[P2P] Mensaje invalido:', err);
      }
    };
  }

  _handleDisconnect() {
    if (!this.isConnected) return;
    this.isConnected = false;
    console.log('[P2P] Desconectado');
    if (this.onDisconnect) this.onDisconnect();
  }

  /**
   * Send a typed message over the data channel.
   * @param {string} type  Message type ('pos', 'block', 'chat')
   * @param {object} data  Payload
   */
  send(type, data) {
    if (!this.dataChannel || this.dataChannel.readyState !== 'open') return;
    try {
      this.dataChannel.send(JSON.stringify({ type, data, t: Date.now() }));
    } catch (err) {
      // Silently ignore send errors (channel closing, etc.)
    }
  }

  /**
   * Send player position + yaw. Called every ~100ms from game loop.
   * @param {{ x: number, y: number, z: number }} pos
   * @param {number} yaw  Camera yaw in radians
   */
  sendPosition(pos, yaw) {
    this.send('pos', {
      x: Math.round(pos.x * 100) / 100,
      y: Math.round(pos.y * 100) / 100,
      z: Math.round(pos.z * 100) / 100,
      yaw: Math.round(yaw * 100) / 100,
    });
  }

  /**
   * Send a block change (place or break).
   * @param {number} x
   * @param {number} y
   * @param {number} z
   * @param {number} blockId  0 = AIR (break), >0 = place
   */
  sendBlockChange(x, y, z, blockId) {
    this.send('block', { x, y, z, b: blockId });
  }

  /**
   * Send a chat message.
   * @param {string} text
   */
  sendChat(text) {
    this.send('chat', { text });
  }

  /**
   * Clean up the existing connection (if any) before creating a new one.
   */
  _cleanup() {
    if (this.dataChannel) {
      try { this.dataChannel.close(); } catch (_) {}
      this.dataChannel = null;
    }
    if (this.peer) {
      try { this.peer.close(); } catch (_) {}
      this.peer = null;
    }
    this.isConnected = false;
    this._pendingCandidates = [];
  }

  /**
   * Fully disconnect and clean up.
   */
  disconnect() {
    this._cleanup();
  }
}
